import React, { useState, useRef } from 'react'
import CommonDialog from '../../../global/dialog'
import EnhancedProfileView from './enhancedProfileView'
import EnhancedProfileDetails from './enhancedProfileDetails'
import EnhancedChangePassword from './enhancedChangePassword'
import EnhancedUpdatePayment from './enhancedUpdatePayment'
import { useGenericQuery } from '../../../../queries/use-generic-query'
import LocalStorageService from '../../../../utils/localStorageServices'
import { useLocation, useHistory } from "react-router-dom"

const localStorageServices = LocalStorageService.getService();

const EnhancedProfile = () => {
  const location = useLocation()
  const history = useHistory()

  const params = new URLSearchParams(location.search)
  const edit = params.get('edit')
  const payment = params.get('payment')

  const currentUser = localStorageServices.getCurrentUser();

  const [showEditModal, setShowEditModal] = useState(!!payment || !!edit);
  const [activeTab, setActiveTab] = useState(payment ? 'UPDATE_PLAN' : 'EDIT_PROFILE');
  const [isSaving, setIsSaving] = useState(false);

  // 🔹 Remove `edit` from URL (v5)
  const removeEditParam = () => {
    const params = new URLSearchParams(location.search);
    params.delete("edit");
    params.delete("payment");

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // 🔹 Centralized close
  const closeDialog = () => {
    setShowEditModal(false);
    removeEditParam();
  };

  /* -------------------- Queries -------------------- */

  const {
    data: userData,
    isLoading,
  } = useGenericQuery({
    endpoint: `user/me`,
    queryKey: ["user", "detail", "me", currentUser?.user?.email],
    queryParams: { email: currentUser?.user?.email },
    enabled: !!currentUser?.user?.email
  });


  const { data: paymentPlans, isLoading: isPlansLoading } =
    useGenericQuery({
      endpoint: "payment/plans",
      queryKey: ["payment", "plans"],
    });


  const profileFormRef = useRef();
  const passwordFormRef = useRef();
  const paymentFormRef = useRef();

  const handleEdit = (tabKey) => {
    setActiveTab(tabKey);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    let activeRef;
    if (activeTab === 'EDIT_PROFILE') activeRef = profileFormRef;
    else if (activeTab === 'CHANGE_PASSWORD') activeRef = passwordFormRef;
    else if (activeTab === 'UPDATE_PLAN') activeRef = paymentFormRef;

    if (activeRef?.current?.submit) {
      try {
        setIsSaving(true);
        await activeRef.current.submit();
        closeDialog();
      } catch (err) {
        console.error("Submit error", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const getDialogConfig = () => {
    switch (activeTab) {
      case 'UPDATE_PLAN':
        return {
          title: "Update Subscription Plan",
          description: "Choose a new plan and update your payment details.",
          okButtonTitle: "Confirm & Pay"
        };
      case 'CHANGE_PASSWORD':
        return {
          title: "Account Security",
          description: "Manage your password and security settings.",
          okButtonTitle: "Update Password"
        };
      default:
        return {
          title: "Edit Profile Details",
          description: "Update your personal information.",
          okButtonTitle: "Save Changes"
        };
    }
  };

  const dialogConfig = getDialogConfig();
  return (
    <>
      <EnhancedProfileView
        onEdit={handleEdit}
        userData={userData}
        isLoading={isLoading}
      />

      <CommonDialog
        title={dialogConfig.title}
        description={dialogConfig.description}
        open={showEditModal}
        setOpen={closeDialog}
        onOk={handleSave}
        onCancel={closeDialog}
        isConfirmButtonLoading={isSaving}
        width={700}
      >
        {(isLoading || isPlansLoading) &&
          <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
            <div className="flex flex-col justify-center items-center gap-1">
              <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
            </div>
          </div>
        }

        {activeTab === 'EDIT_PROFILE' && (
          <EnhancedProfileDetails
            ref={profileFormRef}
            userData={userData}
            onSuccess={closeDialog}
          />
        )}

        {activeTab === 'CHANGE_PASSWORD' && (
          <EnhancedChangePassword
            ref={passwordFormRef}
            onSuccess={closeDialog}
          />
        )}

        {activeTab === 'UPDATE_PLAN' && (
          <EnhancedUpdatePayment
            ref={paymentFormRef}
            userData={userData}
            onSuccess={closeDialog}
            paymentPlans={paymentPlans}
          />
        )}
      </CommonDialog>
    </>
  );
};

export default EnhancedProfile;
